import { observer } from "mobx-react";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import Card from "../../../../../components/layouts/card/card";
import GridLineLayout from "../../../../../components/layouts/grid-line-layout/grid-line-layout";
import { ReturnMethodsCategoryStore } from "../../../stores/return-methods-list-store";
import ReturnMethodLine from "./return-method-line/return-method-line";
import { useState } from "react";
import ReturnMethodStore from "../../../stores/return-method-store";
import { ConfirmModal } from "../../../../../components/modal/components/confirm-modal/confirm-modal";
import { NotificationModal } from "../../../../../components/modal/components/notification-modal/notification-modal";
import useTranslation from "../../../../../translation/hooks/translation-hook";
import config from "../../../../../config";
import { Button } from "../../../../../components/inputs/button/button";
import { BUTTON_VARIANTS } from "../../../../../components/inputs/button-variants";
import IconPlus from "../../../../../components/icons/icon-plus/icon-plus";
import { RETURN_METHOD_CATEGORY } from "../../../return-method-types";
import dashboardSettingsStore from "../../../../../stores/dashboard-settings-store";
import { PROFILE_CAPABILITIES } from "../../../../../models/ProfileCapabilities";


interface ReturnMethodCategoryCardProps {
    title: string,
    returnMethodCategory: ReturnMethodsCategoryStore,
    onAddReturnMethodClicked: () => void,
    onExpandClick: (expanded: boolean) => void,
    isExpanded: boolean;
}

const ReturnMethodCategoryCard: React.FC<ReturnMethodCategoryCardProps> = observer(({ title, returnMethodCategory, onAddReturnMethodClicked, onExpandClick, isExpanded}) => {

    const {setDeletedKeys , save: saveTranslations} = useTranslation();
    const [ returnMethodToDelete, setReturnMethodToDelete ] = useState(undefined as ReturnMethodStore | undefined);
    const [ rulesUsingReturnMethod, setRulesUsingReturnMethod ] = useState(undefined as number | undefined);

    const { getTranslation } = useTranslation();
    
    /** Check if add return method is supported for this category */
    const isAddReturnMethodSupported = () => {
        switch (returnMethodCategory.category) {
            case RETURN_METHOD_CATEGORY.SHIP_WITH_ANY_CARRIER:
                return dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.RETURN_METHOD_SHIP_WITH_ANY_CARRIER).isAddReturnMethodSupported;
            case RETURN_METHOD_CATEGORY.DROP_OFF_LOCATION:
                return dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.RETURN_METHOD_RETURN_TO_DROP_OFF_LOCATION).isAddReturnMethodSupported;
            case RETURN_METHOD_CATEGORY.NO_SHIPMENT_REQUIRED:
                return dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.RETURN_METHOD_NO_SHIPMENT_REQUIRED).isSupported;
            case RETURN_METHOD_CATEGORY.SHIP_WITH_PRE_PAID_LABEL:
                return dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.RETURN_METHOD_SHIP_WITH_PREPAID_LABEL).isSupported;
            default:
                return true;
        }
    }

    const onReturnMethodDelete = async (returnMethod: ReturnMethodStore) => {
        const numOfPolicyRules: number | undefined = await returnMethod.delete();

        if (numOfPolicyRules === undefined) {
            return; // if something went wrong
        }
        if (numOfPolicyRules === 0) {
            returnMethod.setDeletedTranslations(setDeletedKeys);
            saveTranslations();
        }
        else {
            setRulesUsingReturnMethod(numOfPolicyRules);
        }
    }


    return (
    <Card
        toolbar={{
            title: <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{title}</FontBody1>,
        }}
        isExpandable={true}
        isExpanded={isExpanded}
        expandCallback={onExpandClick}
    >
        <GridLineLayout>
            {returnMethodCategory.list.map(returnMethod => (
                <ReturnMethodLine 
                    key={returnMethod.id}
                    returnMethod={returnMethod}
                    deleteCallback={() => setReturnMethodToDelete(returnMethod)}
                />
            )

            )}
        </GridLineLayout>
        {isAddReturnMethodSupported() && (
            <div>
                <Button
                    variant={BUTTON_VARIANTS.Text}
                    text="add return method"
                    leftIcon={IconPlus}
                    onClick={onAddReturnMethodClicked}
                />
            </div>
        )}
        {returnMethodToDelete &&
            <ConfirmModal
                title="Delete return method"
                text={
                    <>
                        You asked to <strong>delete</strong> the
                        return method: <strong>{getTranslation(returnMethodToDelete.name)}</strong>
                    </>
                }
                implicationText="Once deleted the return method and all its translations can’t be restored."
                questionText="Are you sure you want to delete this return method?"
                cancelButton={{
                    title: 'Cancel',
                    onClick: () => {
                        setReturnMethodToDelete(undefined);
                    }
                }}
                okButton={{
                    title: 'Delete',
                    onClick: () => {
                        onReturnMethodDelete(returnMethodToDelete);
                        setReturnMethodToDelete(undefined);
                    }
                }}
            />
        }
        {rulesUsingReturnMethod && rulesUsingReturnMethod > 0 &&
                <NotificationModal
                    title='Delete return method'
                    text={
                        <>
                            <FontBody1>The return method can not be deleted as it is used in </FontBody1><FontBody1 variant={FONT_VARIANT.BOLD}>{rulesUsingReturnMethod}</FontBody1><FontBody1> Policy Rules.</FontBody1>
                            <br/>
                            <FontBody1>To delete this return method, you have to disable the policy rules or select </FontBody1>
                            <a href={`${config.apiUrl}/dashboard/settings/policy-rules`} target="_parent">Go to Policy Rules</a>
                       </>
                    }
                    okButton={{
                        title: 'OK',
                        onClick: () => setRulesUsingReturnMethod(undefined)
                    }}
                 />

            }
       
    </Card>)
});

export default ReturnMethodCategoryCard;
